import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { surface } from '../theme';
import SleepVideoV3Service from '../services/SleepVideoV3Service';
import AuthService from '../services/AuthService';

const FIELDS = [
  { key: 'scriptStructurePrompt', label: 'Script structure prompt', hint: 'In any prompt you can use {VIDEO_TITLE}, {VIDEO_CONTEXT}, and {CHANNEL_NAME} (video title, context, channel name).' },
  { key: 'scriptStructureFile', label: 'Script structure file', hint: 'Content of the file uploaded to ChatGPT at the start.' },
  { key: 'refinementPrompt', label: 'Refinement' },
  { key: 'dedupPrompt', label: 'Deduplication' },
  { key: 'jsonConversionPrompt', label: 'JSON conversion' },
  { key: 'introPrompt', label: 'Introduction' },
  { key: 'readyPrompt', label: 'Ready' },
  { key: 'chapterInstructions', label: 'Chapter instructions', hint: 'Appended to each chapter writing prompt.' },
  { key: 'reviewPrompt', label: 'Review' },
  { key: 'videoDescriptionPrompt', label: 'Video description prompt', hint: 'Sent after the full script is built; response is shown in the Video description box. You can use {VIDEO_TITLE}, {VIDEO_CONTEXT}, {CHANNEL_NAME}.' },
];

const FROZEN_STEPS = ['jsonConversionPrompt'];
const OMITTED_STEPS = ['reviewPrompt', 'videoDescriptionPrompt'];

const initialState = FIELDS.reduce((acc, { key }) => ({ ...acc, [key]: '' }), {});

function SleepPromptSystemEditPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await SleepVideoV3Service.getPromptSystem();
        if (!cancelled) {
          setData({
            scriptStructurePrompt: res.scriptStructurePrompt ?? '',
            scriptStructureFile: res.scriptStructureFile ?? '',
            refinementPrompt: res.refinementPrompt ?? '',
            dedupPrompt: res.dedupPrompt ?? '',
            jsonConversionPrompt: res.jsonConversionPrompt ?? '',
            introPrompt: res.introPrompt ?? '',
            readyPrompt: res.readyPrompt ?? '',
            chapterInstructions: res.chapterInstructions ?? '',
            reviewPrompt: res.reviewPrompt ?? '',
            videoDescriptionPrompt: res.videoDescriptionPrompt ?? '',
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error || err.message || 'Failed to load prompt system',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (key) => (e) => {
    setData((prev) => ({ ...prev, [key]: e.target.value }));
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await SleepVideoV3Service.updatePromptSystem(data);
      setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to save prompt system',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!AuthService.hasRole(['admin', 'superadmin'])) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: 'white',
          mb: 3,
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        Edit Prompt System
      </Typography>

      <Paper
        sx={{
          borderRadius: 2,
          background: surface.glass,
          boxShadow: 2,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sleep-videos')}
              sx={{ textTransform: 'none' }}
            >
              Back to Sleep Videos
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading || saving}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Save
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
              Prompt system saved.
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            FIELDS.map(({ key, label, hint }) => {
              const isFrozen = FROZEN_STEPS.includes(key);
              const isOmitted = OMITTED_STEPS.includes(key);
              return (
                <Accordion
                  key={key}
                  defaultExpanded={key === 'scriptStructurePrompt'}
                  sx={isOmitted ? { opacity: 0.7 } : undefined}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600} color={isOmitted ? 'text.secondary' : undefined}>
                        {label}
                      </Typography>
                      {isFrozen && (
                        <LockIcon fontSize="small" color="action" titleAccess="Frozen" />
                      )}
                      {isOmitted && (
                        <Typography variant="body2" color="text.secondary">
                          Omitted for now
                        </Typography>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {hint && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {hint}
                      </Typography>
                    )}
                    {isFrozen && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Frozen – not editable
                      </Typography>
                    )}
                    {isOmitted && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Omitted for now
                      </Typography>
                    )}
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={20}
                      value={data[key] ?? ''}
                      onChange={handleChange(key)}
                      variant="outlined"
                      size="small"
                      sx={{ mt: 0 }}
                      InputProps={isFrozen || isOmitted ? { readOnly: true } : undefined}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default SleepPromptSystemEditPage;
